// Builds a structured document model from the unified project state.
// The same model renders both the on-screen PDF preview and the Markdown export.
import { getMetrics, metricGap } from "@/lib/metrics";

const fmt = (v) => (v === undefined || v === null || String(v).trim() === "" ? "—" : v);

const yn = (b) => (b ? "Yes" : "No");

// Derives the recommended intervention path from the Phase 2 BEM signals.
// Shared by Phase3.jsx (UI) and buildDocModel (Proposal) so the logic never drifts.
export function deriveSolutionPath(hasLearning, hasNonLearning) {
  if (hasLearning && hasNonLearning) return "blend";
  if (hasLearning) return "learning";
  if (hasNonLearning) return "non_learning";
  return "none";
}

const SOLUTION_PATH_SUMMARY = {
  blend: "Blend of training & non-training interventions",
  learning: "Training intervention (ADDIE)",
  non_learning: "Non-training interventions",
  none: "Pending — complete Cause Analysis",
};


function collectMaterials(pd, keys) {
  const links = [];
  keys.forEach((k) => {
    (pd[k]?.materials || []).forEach((m) => {
      if (m.url || m.label) links.push({ label: m.label || m.url, url: m.url });
    });
  });
  return links;
}

function materialsSection(pd, keys) {
  const links = collectMaterials(pd, keys);
  return {
    heading: "Supporting Materials",
    blocks: links.length ? [{ type: "links", items: links }] : [{ type: "p", text: "No supporting materials linked." }],
  };
}

function gapInfo(p1) {
  const desired = parseFloat(p1.desired_value);
  const current = parseFloat(p1.current_value);
  const gap = !isNaN(desired) && !isNaN(current) ? desired - current : null;
  return { desired, current, gap };
}

function audienceText(p1) {
  const a = p1.audience || {};
  const parts = [
    ["Role", a.role], ["Tenure", a.tenure], ["Location", a.location],
    ["Work Environment", a.work_environment], ["Experience & Background", a.experience],
    ["Language Proficiency", a.language],
  ].filter(([, v]) => v && String(v).trim());
  if (parts.length) return parts.map(([k, v]) => `${k}: ${v}`).join("; ");
  return fmt(p1.target_persona);
}

function goalText(p1) {
  const g = p1.goal || {};
  if ([g.measure, g.percent, g.behavior, g.date].some((v) => v && String(v).trim())) {
    return `${fmt(g.measure)} will ${g.direction || "increase"} ${g.percent ? g.percent + "%" : "—"}${g.date ? " by " + g.date : ""}${g.behavior ? " as people " + g.behavior : ""}`;
  }
  return fmt(p1.business_goals);
}

function tableBlock(columns, rows, emptyText) {
  const empty = [emptyText, ...columns.slice(1).map(() => "")];
  return { type: "table", columns, rows: rows.length ? rows : [empty] };
}

export function buildDocModel(type, project, pd) {
  const p1 = pd.phase1 || {};
  const p2 = pd.phase2 || {};
  const p3 = pd.phase3 || {};
  const addie = p3.addie || {};
  const design = addie.design || {};
  const dev = addie.development || {};
  const nt = p3.non_training || {};
  const p4 = pd.phase4 || {};
  const p5 = pd.phase5 || {};
  const p6 = pd.phase6 || {};
  const objectives = design.objectives || [];
  const p2env = p2?.environmental || {};
  const p2ind = p2?.individual || {};
  const hasLearning = !!p2ind.knowledge_skills;
  const hasNonLearning = !!(p2env.information || p2env.tools || p2env.incentives || p2ind.motives);
  const solutionPath = deriveSolutionPath(hasLearning, hasNonLearning);
  const metrics = getMetrics(p1);
  const metricsTableBlock = {
    type: "table",
    columns: ["Metric", "Current", "Target", "Unit", "Gap"],
    rows: metrics.length
      ? metrics.map((m) => [fmt(m.name), fmt(m.current), fmt(m.target), fmt(m.unit), metricGap(m) === null ? "—" : String(metricGap(m))])
      : [["No metrics defined.", "", "", "", ""]],
  };

  const meta = {
    project: project?.name || "Untitled Project",
    date: new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" }),
    logo: p1.company_logo || "",
    agency_logo: (project?.branding || {}).agency_logo || "",
  };

  if (type === "analysis") {
    return {
      title: "Performance & Cause Analysis Report",
      subtitle: "ATD HPI · Phases 1–2",
      meta,
      sections: [
        ...(String(p1.executive_summary || "").trim() ? [{ heading: "Executive Summary", blocks: [{ type: "p", text: p1.executive_summary }] }] : []),
        {
          heading: "1. Performance Analysis",
          blocks: [
            { type: "kv", items: [
              { k: "Business Goal", v: goalText(p1) },
              ...(String(p1.business_goals || "").trim() ? [{ k: "Business Context", v: p1.business_goals }] : []),
              { k: "Target Audience", v: audienceText(p1) },
              { k: "Desired Performance State", v: fmt(p1.desired_state) },
              { k: "Current Performance State", v: fmt(p1.current_state) },
            ]},
            { type: "subheading", text: "Key Stakeholders" },
            tableBlock(["Stakeholder", "Role", "Contribution"],
              (p1.stakeholders || []).map((s) => [fmt(s.name), fmt(s.role), fmt(s.contribution)]),
              "No stakeholders documented."),
            { type: "subheading", text: "Performance Metrics & Gaps" },
            metricsTableBlock,
            { type: "subheading", text: "Data Collection — Sources & Findings" },
            tableBlock(["Data Source", "Key Finding(s)"],
              (p1.data_sources || []).map((d) => [fmt(d.source), fmt(d.finding)]),
              "No data sources documented."),
          ],
        },
        {
          heading: "2. Cause Analysis",
          blocks: [
            { type: "table",
              columns: ["Category", "Factor", "Flagged as Cause"],
              rows: [
                ["Environmental", "Information", yn(p2?.environmental?.information)],
                ["Environmental", "Tools & Resources", yn(p2?.environmental?.tools)],
                ["Environmental", "Incentives", yn(p2?.environmental?.incentives)],
                ["Individual", "Knowledge / Skills", yn(p2?.individual?.knowledge_skills)],
                ["Individual", "Motives", yn(p2?.individual?.motives)],
              ],
            },
            { type: "subheading", text: "Training Needs" },
            tableBlock(["Topic", "Need", "Evidence"],
              (p2.training_needs || []).map((n) => [fmt(n.topic), fmt(n.need), fmt(n.evidence)]),
              "No training needs documented."),
            { type: "subheading", text: "Non-Training Needs" },
            tableBlock(["Topic", "Need", "Evidence"],
              (p2.non_training_needs || []).map((n) => [fmt(n.topic), fmt(n.need), fmt(n.evidence)]),
              "No non-training needs documented."),
            { type: "subheading", text: "Diagnostic Notes" },
            { type: "p", text: fmt(p2.notes) },
          ],
        },
        materialsSection(pd, ["phase1", "phase2"]),
      ],
    };
  }

  if (type === "proposal") {
    const sections = [
      {
        heading: "1. Business Objective",
        blocks: [
          { type: "kv", items: [{ k: "Business Goal", v: goalText(p1) }, { k: "Target Audience", v: audienceText(p1) }] },
          { type: "subheading", text: "Performance Metrics & Gaps" },
          metricsTableBlock,
        ],
      },
      {
        heading: "2. Intervention Diagnosis (Gilbert's BEM)",
        blocks: [
          { type: "table",
            columns: ["Cause / Category", "Diagnosis", "Tag"],
            rows: (p3.interventions || []).length
              ? p3.interventions.map((it) => [fmt(it.category), fmt(it.diagnosis), fmt(it.tag)])
              : [["No interventions diagnosed.", "", ""]],
          },
        ],
      },
    ];

    const PATH_LABEL = { learning: "Learning Solution", non_learning: "Non-Learning Solution", blend: "Blended Solution (Training + Non-Training)", none: "Pending — complete Cause Analysis" };
    let n = 2;
    sections.push({
      heading: "3. Recommended Approach",
      blocks: [{ type: "kv", items: [
        { k: "Solution Path", v: PATH_LABEL[solutionPath] },
        { k: "Rationale", v: solutionPath === "blend"
          ? "Both capability (Knowledge/Skills) and environmental/motivational causes were confirmed, warranting a blend of training and non-training interventions."
          : solutionPath === "learning"
          ? "Knowledge/Skills was confirmed as a root cause, warranting a training intervention designed via ADDIE."
          : solutionPath === "non_learning"
          ? "Causes are environmental/motivational; non-training interventions are recommended to close the gap."
          : "No causes have been confirmed yet in Phase 2." },
      ]}],
    });
    n = 3;


    if (hasLearning) {
      sections.push({
        heading: `${++n}. Recommended Solution — Training Intervention (ADDIE)`,
        blocks: [{ type: "p", text: "Knowledge/Skills was confirmed as a root cause. A structured training solution is recommended, designed and developed via the ADDIE methodology." }],
      });
      sections.push({
        heading: `${++n}. High-Level Design — Learning Objectives`,
        blocks: [
          { type: "table",
            columns: ["#", "Bloom's Level", "Learning Objective"],
            rows: objectives.length
              ? objectives.map((o, i) => [String(i + 1), fmt(o.bloom_level), fmt(o.text)])
              : [["—", "—", "No learning objectives defined."]],
          },
          { type: "subheading", text: "Course Outline" },
          { type: "p", text: fmt(design.course_outline) },
        ],
      });
      sections.push({
        heading: `${++n}. Development — Media Assets & QA`,
        blocks: [
          { type: "subheading", text: "Media Asset Tracker" },
          { type: "table",
            columns: ["Asset", "Type", "Status", "Owner"],
            rows: (dev.media_assets || []).length
              ? dev.media_assets.map((a) => [fmt(a.name), fmt(a.type), fmt(a.status), fmt(a.owner)])
              : [["No media assets tracked.", "", "", ""]],
          },
          { type: "subheading", text: "QA Checklist" },
          { type: "checklist", items: (dev.qa_checklist || []).map((q) => ({ text: fmt(q.item), done: !!q.done })) },
        ],
      });
      const solutions = p3.solutions || {};
      sections.push({
        heading: `${++n}. Recommended Solution - Training`,
        blocks: [
          tableBlock(["Title", "Need Addressed", "Description", "Proposed Outcome(s)"],
            (solutions.learning || []).map((s) => [fmt(s.title), fmt(s.need), fmt(s.description), fmt(s.outcomes)]),
            "No training solutions documented."),
        ],
      });
    }

    if (hasNonLearning) {
      sections.push({
        heading: `${++n}. Recommended Solution — Non-Training Interventions`,
        blocks: [
          { type: "p", text: "Environmental and/or motivational causes were confirmed. The following non-training interventions are recommended to close the performance gap." },
          { type: "kv", items: [
            { k: "Job Aids", v: fmt(nt.job_aids) },
            { k: "Process Redesign", v: fmt(nt.process_redesign) },
            { k: "Compensation / Incentive Changes", v: fmt(nt.compensation_changes) },
          ]},
        ],
      });
    }

    return { title: "Integrated Solution Proposal & HLDD", subtitle: "ATD HPI · Phase 3 + ADDIE · Phase 1 Goals", meta, sections: [...sections, materialsSection(pd, ["phase3"])] };
  }

  if (type === "implementation") {
    return {
      title: "Implementation & Change Management Plan",
      subtitle: "ATD HPI · Phases 4–5",
      meta,
      sections: [
        {
          heading: "1. Implementation Timeline",
          blocks: [
            { type: "table",
              columns: ["Task", "Owner", "Start", "End", "Status"],
              rows: (p4.tasks || []).length
                ? p4.tasks.map((t) => [fmt(t.name), fmt(t.owner), fmt(t.start), fmt(t.end), fmt(t.status)])
                : [["No implementation tasks defined.", "", "", "", ""]],
            },
          ],
        },
        {
          heading: "2. Stakeholder Communication Plan",
          blocks: [
            { type: "table",
              columns: ["Stakeholder", "Key Message", "Channel", "Frequency"],
              rows: (p5.communication_plan || []).length
                ? p5.communication_plan.map((c) => [fmt(c.stakeholder), fmt(c.message), fmt(c.channel), fmt(c.frequency)])
                : [["No communications planned.", "", "", ""]],
            },
          ],
        },
        {
          heading: "3. Risk & Resistance Mitigation Log",
          blocks: [
            { type: "table",
              columns: ["Risk / Resistance", "Likelihood", "Mitigation"],
              rows: (p5.risks || []).length
                ? p5.risks.map((r) => [fmt(r.risk), fmt(r.likelihood), fmt(r.mitigation)])
                : [["No risks logged.", "", ""]],
            },
          ],
        },
        {
          heading: "4. Project Scope — Deliverables",
          blocks: [
            tableBlock(["Deliverable", "Details", "Due"],
              (p4.deliverables || []).map((d) => [fmt(d.name), fmt(d.details), fmt(d.due)]),
              "No deliverables defined."),
            { type: "subheading", text: "Out of Scope" },
            { type: "p", text: fmt(p4.out_of_scope) },
          ],
        },
        materialsSection(pd, ["phase4", "phase5"]),
      ],
    };
  }

  if (type === "workbook") {
    const tr = p1.training_request || {};
    const kickoff = p1.kickoff_questions || [];
    const rootCauses = p2.root_causes || [];
    const goalStmt = String(p1.business_goal_statement || "").trim() ? p1.business_goal_statement : goalText(p1);
    return {
      title: "Data Workbook",
      subtitle: "Analysis & Data Collection · ATD HPI Phases 1–2",
      meta,
      sections: [
        {
          heading: "1. Training Request Form",
          blocks: [
            tableBlock(["Question", "Notes"], [
              ["What is the problem?", fmt(tr.problem)],
              ["Will learning solutions help?", fmt(tr.learning_help)],
              ["What do we still need to know?", fmt(tr.still_need)],
            ], "—"),
          ],
        },
        {
          heading: "2. Project Kickoff Call",
          blocks: [
            { type: "subheading", text: "Business Goal Statement" },
            { type: "p", text: goalStmt },
            { type: "subheading", text: "Analysis Questions" },
            tableBlock(["Analysis Type", "Question(s)"],
              kickoff.map((q) => [fmt(q.type), fmt(q.question)]),
              "No kickoff questions documented."),
          ],
        },
        {
          heading: "3. Data Collection",
          blocks: [
            tableBlock(["Data Source", "Finding(s) & Insights"],
              (p1.data_sources || []).map((d) => [fmt(d.source), fmt(d.finding)]),
              "No data sources documented."),
          ],
        },
        {
          heading: "4. Data Analysis — Root Cause",
          blocks: [
            tableBlock(["Need", "Evidence", "Type of Gap", "Type of Need"],
              rootCauses.map((r) => [fmt(r.need), fmt(r.evidence), fmt(r.gap_type), fmt(r.need_type)]),
              "No root cause analysis documented."),
          ],
        },
        materialsSection(pd, ["phase1", "phase2"]),
      ],
    };
  }

  if (type === "design") {
    const blueprint = design.blueprint || [];
    const arcs = design.arcs || {};
    const transfer = p5.transfer_plan || {};
    const reviewCycle = p4.review_cycle || {};
    const rollout = p4.rollout_plan || [];
    const kirk = p6.kirkpatrick || {};
    const kLevels = [["Reaction", "reaction"], ["Learning", "learning"], ["Behavior", "behavior"], ["Results", "results"]];
    const solList = (p3.solutions?.learning || []).map((s) => s.title).filter(Boolean);
    return {
      title: "Design Document",
      subtitle: "Instructional Design Blueprint · ATD HPI Phases 3–6",
      meta,
      sections: [
        ...(String(p1.executive_summary || "").trim() ? [{ heading: "Executive Summary", blocks: [{ type: "p", text: p1.executive_summary }] }] : []),
        {
          heading: "1. Project Overview",
          blocks: [{ type: "kv", items: [
            { k: "Business Problem", v: fmt(p1.current_state) },
            { k: "Business Goal", v: goalText(p1) },
            { k: "Target Audience", v: audienceText(p1) },
            { k: "Proposed Solution", v: solList.length ? solList.join("; ") : SOLUTION_PATH_SUMMARY[solutionPath] },
          ]}],
        },
        {
          heading: "2. Instructional Blueprint",
          blocks: blueprint.length
            ? blueprint.flatMap((b, i) => [
                { type: "subheading", text: `Performance Objective #${i + 1}` },
                { type: "kv", items: [
                  { k: "Objective", v: fmt(b.objective) },
                  { k: "Enabling Objectives", v: fmt(b.enabling) },
                  { k: "Assessment Plan", v: fmt(b.assessment) },
                  { k: "Instructional Strategy", v: fmt(b.strategy) },
                  { k: "Delivery Method", v: fmt(b.delivery) },
                  { k: "Practice & Feedback Plan", v: fmt(b.practice) },
                ]},
              ])
            : [{ type: "p", text: "No performance objectives documented." }],
        },
        {
          heading: "3. Motivation Design (ARCS Model)",
          blocks: [{ type: "kv", items: [
            { k: "Attention", v: fmt(arcs.attention) },
            { k: "Relevance", v: fmt(arcs.relevance) },
            { k: "Confidence", v: fmt(arcs.confidence) },
            { k: "Satisfaction", v: fmt(arcs.satisfaction) },
          ]}],
        },
        {
          heading: "4. Evaluation & Impact Plan (Kirkpatrick's 4 Levels)",
          blocks: [
            tableBlock(["Level", "Success Criteria", "Planned Method(s)", "Timing"],
              kLevels.map(([label, key]) => { const k = kirk[key] || {}; return [label, fmt(k.criteria), fmt(k.method), fmt(k.timing)]; }),
              "No evaluation plan documented."),
          ],
        },
        {
          heading: "5. Learning Transfer Plan",
          blocks: [{ type: "kv", items: [
            { k: "Environmental Supports", v: fmt(transfer.environmental_supports) },
            { k: "Manager & Peer Reinforcement", v: fmt(transfer.manager_peer) },
            { k: "Follow-up & Practice Opportunities", v: fmt(transfer.follow_up) },
            { k: "Self-Monitoring Opportunities", v: fmt(transfer.self_monitoring) },
          ]}],
        },
        {
          heading: "6. Review & Iteration Cycle",
          blocks: [
            { type: "subheading", text: "Stakeholder Review Plan" },
            tableBlock(["Review Milestone", "Who Reviews", "What's Being Reviewed"],
              (reviewCycle.plan || []).map((r) => [fmt(r.milestone), fmt(r.who), fmt(r.what)]),
              "No review milestones documented."),
            { type: "subheading", text: "Iteration & Approval Cycle" },
            { type: "p", text: fmt(reviewCycle.iteration_approval) },
            { type: "subheading", text: "Version Control" },
            { type: "p", text: fmt(reviewCycle.version_control) },
          ],
        },
        {
          heading: "7. Implementation & Rollout Plan",
          blocks: [
            { type: "subheading", text: "Rollout Plan" },
            tableBlock(["Phase", "Key Activities", "Owner(s)", "Timeline"],
              rollout.map((r) => [fmt(r.phase), fmt(r.activities), fmt(r.owner), fmt(r.timeline)]),
              "No rollout phases documented."),
            { type: "subheading", text: "Communication Plan" },
            tableBlock(["Stakeholder", "Key Message", "Channel", "Frequency"],
              (p5.communication_plan || []).map((c) => [fmt(c.stakeholder), fmt(c.message), fmt(c.channel), fmt(c.frequency)]),
              "No communications planned."),
            { type: "subheading", text: "Contingency Plan" },
            { type: "p", text: fmt(p4.contingency_plan) },
          ],
        },
        materialsSection(pd, ["phase3", "phase4", "phase5"]),
      ],
    };
  }

  // ROI / Impact
  const results = p6.metric_results || {};
  const impact = p6.objective_impact || {};
  const impactFor = (o, i) => impact[o.id] || impact[i] || {};
  const metricRows = metrics.length
    ? metrics.map((m) => {
        const baseline = parseFloat(m.current);
        const targetV = parseFloat(m.target);
        const postV = parseFloat(results[m.id]);
        const ig = !isNaN(baseline) && !isNaN(targetV) ? targetV - baseline : null;
        const gc = !isNaN(postV) && !isNaN(baseline) ? postV - baseline : null;
        const pct = gc !== null && ig ? Math.round((gc / ig) * 100) : null;
        return [fmt(m.name), fmt(m.unit), isNaN(baseline) ? "—" : String(baseline), isNaN(targetV) ? "—" : String(targetV), isNaN(postV) ? "—" : String(postV), pct === null ? "—" : `${pct}%`];
      })
    : [["No metrics defined.", "", "", "", "", ""]];

  return {
    title: "Executive ROI Impact Report",
    subtitle: "ATD HPI · Phase 1 Gap vs Phase 6 Results",
    meta,
    sections: [
      {
        heading: "1. All Metrics — Baseline vs Target vs Post-Intervention",
        blocks: [
          { type: "table",
            columns: ["Metric", "Unit", "Baseline", "Target", "Post-Intervention", "Gap Closed"],
            rows: metricRows,
          },
        ],
      },
      {
        heading: "2. Objective Impact (Kirkpatrick L2 / L3)",
        blocks: [
          { type: "table",
            columns: ["Learning Objective", "Bloom's Level", "L2: Assessment", "L3: Behavior"],
            rows: objectives.length
              ? objectives.map((o, i) => { const im = impactFor(o, i); return [fmt(o.text), fmt(o.bloom_level), fmt(im.level2), fmt(im.level3)]; })
              : [["No objectives to evaluate.", "", "", ""]],
          },
        ],
      },
      {
        heading: "3. Evaluation Summary",
        blocks: [
          { type: "subheading", text: "Post-Intervention Metrics" },
          { type: "p", text: fmt(p6.post_metrics) },
          { type: "subheading", text: "Notes & Recommendations" },
          { type: "p", text: fmt(p6.notes) },
        ],
      },
      materialsSection(pd, ["phase6"]),
    ],
  };
}

const DECK_PARTS = ["workbook", "analysis", "proposal", "design", "implementation", "roi"];

// Bundles all deliverables into a single client-ready deck document with a cover page + contents.
export function buildCombinedDocModel(project, pd) {
  const date = new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
  const projectName = project?.name || "Untitled Project";
  const p1 = pd.phase1 || {};
  const sections = [];
  const toc = [];
  DECK_PARTS.forEach((type, idx) => {
    const m = buildDocModel(type, project, pd);
    const partTitle = `Part ${idx + 1} — ${m.title}`;
    toc.push(partTitle);
    sections.push({
      heading: partTitle,
      part: true,
      pageBreak: true,
      blocks: [{ type: "p", text: m.subtitle }],
    });
    m.sections.forEach((s) => sections.push(s));
  });
  return {
    title: "Full Client Deck",
    subtitle: "ATD 6-Phase HPI · Complete Engagement Report",
    cover: true,
    toc,
    meta: {
      project: projectName,
      date,
      company_name: p1.company_name || "",
      business_unit: p1.business_unit || "",
      logo: p1.company_logo || "",
      agency_logo: (project?.branding || {}).agency_logo || "",
    },
    sections,
  };
}

export function modelToMarkdown(model) {
  const lines = [];
  lines.push(`# ${model.title}`);
  lines.push("");
  lines.push(`*${model.subtitle}*`);
  lines.push("");
  lines.push(`**Project:** ${model.meta.project}  `);
  lines.push(`**Generated:** ${model.meta.date}`);
  lines.push("");

  model.sections.forEach((sec) => {
    lines.push(`## ${sec.heading}`);
    lines.push("");
    sec.blocks.forEach((b) => {
      if (b.type === "p") {
        lines.push(b.text);
        lines.push("");
      } else if (b.type === "subheading") {
        lines.push(`### ${b.text}`);
        lines.push("");
      } else if (b.type === "kv") {
        b.items.forEach((it) => lines.push(`- **${it.k}:** ${it.v}`));
        lines.push("");
      } else if (b.type === "callout") {
        lines.push(`> **${b.label}:** ${b.value}`);
        lines.push("");
      } else if (b.type === "checklist") {
        if (!b.items.length) { lines.push("_No items._"); }
        b.items.forEach((it) => lines.push(`- [${it.done ? "x" : " "}] ${it.text}`));
        lines.push("");
      } else if (b.type === "links") {
        if (!b.items.length) { lines.push("_No supporting materials._"); }
        b.items.forEach((it) => lines.push(`- [${it.label || it.url}](${it.url || ""})`));
        lines.push("");
      } else if (b.type === "list") {
        b.items.forEach((it) => lines.push(`- ${it}`));
        lines.push("");
      } else if (b.type === "table") {
        lines.push(`| ${b.columns.join(" | ")} |`);
        lines.push(`| ${b.columns.map(() => "---").join(" | ")} |`);
        b.rows.forEach((r) => lines.push(`| ${r.map((c) => String(c).replace(/\n/g, " ")).join(" | ")} |`));
        lines.push("");
      }
    });
  });

  return lines.join("\n");
}
